"use client";

import { useState } from "react";
import Image from "next/image";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/shadcn/ui/dialog";
import SignInForm from "@/components/shop/auth/forms/SignInForm";
import SignUpForm from "@/components/shop/auth/forms/SignUpForm";
import ResetPasswordForm from "@/components/shop/auth/forms/ResetPasswordForm";

import userWhite from "@/public/shop/user.svg";
import userBlack from "@/public/shop/user-black.svg";

interface AuthModalProps {
  theme: "transparent" | "light";
  defaultMode?: "login" | "register" | "forgot-password";
  children?: React.ReactNode;
}

export default function AuthModal({
  theme,
  defaultMode = "login",
  children,
}: AuthModalProps) {
  const [mode, setMode] = useState<"login" | "register" | "forgot-password">(
    defaultMode,
  );
  const [open, setOpen] = useState(false);

  const handleLoginSuccess = () => {
    setOpen(false);
  };

  const handleRegisterSuccess = () => {
    setMode("login");
  };

  const handleResetPasswordSuccess = () => {
    setMode("login");
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild className="cursor-pointer">
        {children ? (
          children
        ) : (
          <button className="group h-full p-[2rem] hover:bg-white transition-colors">
            <Image
              src={theme === "transparent" ? userWhite : userBlack}
              alt="Benutzer"
              className={
                theme === "transparent"
                  ? "w-[3rem] h-[3rem] min-w-[3rem] min-h-[3rem] group-hover:invert transition-all"
                  : "w-[3rem] h-[3rem] min-w-[3rem] min-h-[3rem]"
              }
            />
          </button>
        )}
      </DialogTrigger>
      <DialogContent className="w-full max-w-[95svw] lg:max-w-[60rem] rounded-none bg-[#F3F2F1] text-black p-[2rem] lg:p-[3rem] [&_.close-btn]:top-[2.5rem] [&_.close-btn]:right-[2.5rem]">
        <DialogHeader className="mb-[2rem]">
          <DialogTitle className="text-[2.5rem] font-funnel font-[500]">
            {mode === "login"
              ? "Anmelden"
              : mode === "register"
                ? "Konto erstellen"
                : "Passwort zurücksetzen"}
          </DialogTitle>
        </DialogHeader>
        <DialogDescription className="hidden"></DialogDescription>

        <div className="">
          {mode === "login" && (
            <SignInForm
              onForgotPassword={() => setMode("forgot-password")}
              onSuccess={handleLoginSuccess}
            />
          )}

          {mode === "register" && (
            <SignUpForm onSuccess={handleRegisterSuccess} />
          )}

          {mode === "forgot-password" && (
            <ResetPasswordForm onSuccess={handleResetPasswordSuccess} />
          )}

          {/* Toggle Mode */}
          <div className="mt-[4rem] text-center">
            {mode === "login" && (
              <p className="text-[1.75rem] text-black/50 mb-0">
                Noch kein Konto?{" "}
                <button
                  type="button"
                  onClick={() => setMode("register")}
                  className="text-black/80 hover:underline"
                >
                  Konto erstellen
                </button>
              </p>
            )}

            {mode === "register" && (
              <p className="text-[1.75rem] text-black/50 mb-0">
                Bereits ein Konto?{" "}
                <button
                  type="button"
                  onClick={() => setMode("login")}
                  className="text-black/80 hover:underline"
                >
                  Anmelden
                </button>
              </p>
            )}

            {mode === "forgot-password" && (
              <p className="text-[1.75rem] text-black/50 mb-0">
                Erinnern Sie sich an Ihr Passwort?{" "}
                <button
                  type="button"
                  onClick={() => setMode("login")}
                  className="text-black/80 hover:underline"
                >
                  Zurück zur Anmeldung
                </button>
              </p>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
